import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { TripDetail } from '@/modules/booking/model';
import { Api, Site, SiteCheckpointId } from '@/modules/common/model';

import useMediaQuery from '../../../plugins/useMediaQuery';
import { getSiteWithId } from '../utils';
import DayPicker from './DayPicker';
import HourPicker from './HourPicker';
import SiteDropdown from './SiteDropdown';

export interface FormContent {
  site?: string;
  checkpoint?: SiteCheckpointId;
  date?: Date;
  hour?: string;
  tripNumber?: string;
  tripDetail?: TripDetail;
}

interface Props {
  content: FormContent;
  sites: Array<Api.Site>;
  slots?: Api.SiteSlots;
  forceValidation?: boolean;
  displaySite: boolean;
  displayNotice?: boolean;
  isFetchingSlots: boolean;
  onChange: (content: FormContent) => void;
  displayArrivalSiteButton: boolean;
  onDifferentArrivalSiteEnable: () => void;
  prefix: 'start' | 'end';
  notice: string;
}

const transportSuffix: Record<Site.Kind, string> = {
  [Site.Kind.Airport]: 'flight',
  [Site.Kind.Station]: 'train',
};

const typeTravel: Record<Props['prefix'], string> = {
  start: 'departure',
  end: 'arrival',
};

export const GenericForm: FC<Props> = ({
  content,
  sites,
  slots,
  forceValidation,
  displaySite,
  displayNotice,
  isFetchingSlots,
  onChange,
  displayArrivalSiteButton,
  onDifferentArrivalSiteEnable,
  prefix,
  notice,
}: Props) => {
  const { t } = useTranslation();
  const isCompactModule = useMediaQuery('(max-width: 960px)');

  const { site, date, hour, tripDetail } = content || {};
  const currentSite = getSiteWithId(sites, site);
  const siteType = (currentSite && currentSite.type) || Site.Kind.Airport;

  const prefixDate = `fast-${isCompactModule ? 'compact' : 'extended'}`;
  const dateLabel = `booking-${prefixDate}-${prefix}_date`;

  const hourFullLabel =
    prefix === 'start' ? 'booking-hour_start_label' : `booking-hour_end_${transportSuffix[siteType]}_label`;
  const hourPlaceholder = isCompactModule ? hourFullLabel : 'hour';
  const hourLabel = isCompactModule ? null : hourFullLabel;

  const valueChanged = (name: string) => (value: string | Date) => onChange({ ...content, [name]: value });

  const onDateChange = useCallback((value: Date) => onChange({ ...content, date: value }), [onChange, content]);

  return (
    <React.Fragment>
      <div className="form">
        {displaySite && (
          <>
            <SiteDropdown
              name="site"
              label={t(`booking-${typeTravel[prefix]}_site`)}
              prefix={prefix}
              sites={sites}
              value={site}
              forceValidation={forceValidation}
              onChange={valueChanged('site')}
            />
            {displayArrivalSiteButton && !!site && (
              <button className="arrivalSiteText" type="button" onClick={() => onDifferentArrivalSiteEnable()}>
                {t('booking-arrival_site_modal_title')}
              </button>
            )}
          </>
        )}
        <DayPicker
          prefix={prefix}
          date={date}
          label={t(dateLabel)}
          placeholder={t(`${dateLabel}_placeholder`)}
          tripDetail={tripDetail}
          forceValidation={forceValidation}
          onDateChange={onDateChange}
        />
        <HourPicker
          prefix={prefix}
          date={date}
          hour={hour}
          label={hourLabel}
          placeholder={hourPlaceholder}
          slots={slots}
          tripDetail={tripDetail}
          forceValidation={forceValidation}
          notice={displayNotice ? notice : undefined}
          onHourChange={valueChanged('hour')}
          isFetchingSlots={isFetchingSlots}
        />
      </div>
    </React.Fragment>
  );
};

export default GenericForm;
